// CLI v0.8 — `cloudgrid env unset [<name>] KEY1 KEY2 ...`. Spec v17 §13.8.
//
// Destructive — confirmation prompt is the default per audit Part B.4 #5.
// `--yes` skips it; non-TTY without `--yes` errors with CONFIRMATION_REQUIRED
// so CI doesn't block on a prompt.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import inquirer from 'inquirer';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';
import { existsSync } from 'fs';
import { join } from 'path';

interface PatchResponse {
  status: string;
  keys_set: string[];
  keys_unset: string[];
  restarting: boolean;
}

export interface RunEnvUnsetOpts {
  client: IV2Client;
  name: string | undefined;
  keys: string[];
  yes?: boolean;
  cwd?: string;
  isTTY?: boolean;
  promptFn?: (message: string) => Promise<boolean>;
}

export async function runEnvUnset(opts: RunEnvUnsetOpts): Promise<void> {
  if (opts.keys.length === 0) {
    throw new V2ApiError(400, 'VALIDATION_FAILED', 'Expected at least one key to unset.');
  }
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);

  if (!opts.yes) {
    const isTTY = opts.isTTY ?? Boolean(process.stdin.isTTY);
    if (!isTTY) {
      throw new V2ApiError(
        400, 'CONFIRMATION_REQUIRED',
        'Unset needs confirmation. Re-run with --yes in non-interactive shells.',
      );
    }
    const n = opts.keys.length;
    const ok = await (opts.promptFn ?? defaultConfirm)(
      `Unset ${n} env var${n === 1 ? '' : 's'} on ${target.name}?`,
    );
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  const resp = await opts.client.patch<PatchResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/env`,
    { unset: opts.keys },
  );
  const n = resp.keys_unset.length;
  process.stdout.write(`Unset ${n} env var${n === 1 ? '' : 's'}.\n`);
  if (resp.restarting) process.stdout.write('Rolling restart dispatched.\n');
}

/**
 * Argv splitter for `env unset`.
 *
 * Keys and entity slugs share the same shape (no disambiguator), so we
 * disambiguate by context:
 *  - if the user is inside a `cloudgrid.yaml` directory, all positionals
 *    are keys (entity comes from link.json).
 *  - otherwise the first positional is the entity name and the rest are keys.
 *
 * Tested via `splitNameAndKeys` so callers can override `cwd`.
 */
export function splitNameAndKeys(
  args: string[],
  cwd: string,
): { name: string | undefined; keys: string[] } {
  const inFolder = existsSync(join(cwd, 'cloudgrid.yaml')) || existsSync(join(cwd, '.cloudgrid', 'link.json'));
  if (inFolder) return { name: undefined, keys: args };
  if (args.length < 2) return { name: undefined, keys: args };
  return { name: args[0], keys: args.slice(1) };
}

async function defaultConfirm(message: string): Promise<boolean> {
  const { ok } = await inquirer.prompt([
    { type: 'confirm', name: 'ok', message, default: false },
  ]);
  return Boolean(ok);
}

export const envUnsetCommand: CommanderCommand = new Command('unset')
  .description(
    'Unset env vars on a runtime (admin only). ' +
    'In-folder: cloudgrid env unset KEY1 KEY2. ' +
    'Out-of-folder: cloudgrid env unset <name> KEY1 KEY2.',
  )
  .argument('<args...>', 'Keys to unset, optionally preceded by an entity name')
  .option('-y, --yes', 'Skip the confirmation prompt')
  .action(async (args: string[], opts: { yes?: boolean }) => {
    try {
      const { name, keys } = splitNameAndKeys(args, process.cwd());
      const client = await getV2Client();
      await runEnvUnset({ client, name, keys, yes: opts.yes });
    } catch (err) {
      await handleV2Error(err);
    }
  });
